import type {
  CertificationState,
  SystemEvidenceRecord,
  SystemScenarioRunRecord,
  SystemWorkspaceDefinition,
} from './SystemWorkspaceContracts';

import {
  projectSystemCertification,
  projectSystemEvidence,
  projectSystemStatus,
} from './SystemStatusProjectionEngine';

import {
  listMembershipBillingScenarios,
  listMembershipBillingSimulationEvidence,
  listMembershipBillingSimulationRuns,
} from './MembershipBillingSimulatorRuntime';

export const MEMBERSHIP_BILLING_SYSTEM_ID =
  'membership-billing';

const lockedEvidence:
  SystemEvidenceRecord[] = [
    {
      id: 'bp-004e2a-source',
      systemId:
        MEMBERSHIP_BILLING_SYSTEM_ID,
      mode: 'HISTORICAL',
      category: 'authority',
      source:
        'BP-004E2A verification',
      summary:
        'Membership backend recovery and entitlement persistence authority passed 11 of 11 checks.',
      observedAt:
        '2026-07-18T00:00:00.000Z',
      critical: true,
      passed: true,
      correlationId:
        'bp-004e2-authority-chain',
      payload: {
        milestone: 'BP-004E2A',
        checksPassed: 11,
        checksTotal: 11,
      },
    },
    {
      id: 'bp-004e2b1-ledger',
      systemId:
        MEMBERSHIP_BILLING_SYSTEM_ID,
      mode: 'HISTORICAL',
      category: 'authority',
      source:
        'BP-004E2B1 verification',
      summary:
        'Membership ledger and recovery ordering passed 9 of 9 checks.',
      observedAt:
        '2026-07-18T00:01:00.000Z',
      critical: true,
      passed: true,
      correlationId:
        'bp-004e2-authority-chain',
      payload: {
        milestone: 'BP-004E2B1',
        checksPassed: 9,
        checksTotal: 9,
      },
    },
    {
      id: 'bp-004e2b2-receipts',
      systemId:
        MEMBERSHIP_BILLING_SYSTEM_ID,
      mode: 'HISTORICAL',
      category: 'certification',
      source:
        'BP-004E2B2 verification',
      summary:
        'Membership receipt projection passed 15 of 15 checks.',
      observedAt:
        '2026-07-18T00:02:00.000Z',
      critical: true,
      passed: true,
      correlationId:
        'bp-004e2-authority-chain',
      payload: {
        milestone: 'BP-004E2B2',
        checksPassed: 15,
        checksTotal: 15,
      },
    },
    {
      id: 'bp-004e2c-source-certification',
      systemId:
        MEMBERSHIP_BILLING_SYSTEM_ID,
      mode: 'HISTORICAL',
      category: 'certification',
      source:
        'BP-004E2C verification',
      summary:
        'Membership source certification passed 24 of 24 checks; live runtime certification remains pending.',
      observedAt:
        '2026-07-18T00:03:00.000Z',
      critical: true,
      passed: true,
      correlationId:
        'bp-004e2-authority-chain',
      payload: {
        milestone: 'BP-004E2C',
        checksPassed: 24,
        checksTotal: 24,
        liveRuntimeCertified: false,
      },
    },
  ];

function latestRunsByScenario(
  runs: SystemScenarioRunRecord[],
): Map<string, SystemScenarioRunRecord> {
  const latest =
    new Map<string, SystemScenarioRunRecord>();

  for (const run of runs) {
    if (!latest.has(run.scenarioId)) {
      latest.set(run.scenarioId, run);
    }
  }

  return latest;
}

export function projectMembershipBillingWorkspace(
  workspace: SystemWorkspaceDefinition,
) {
  if (
    workspace.id !==
    MEMBERSHIP_BILLING_SYSTEM_ID
  ) {
    throw new Error(
      'Membership workspace projection received the wrong system authority.',
    );
  }

  const scenarios =
    listMembershipBillingScenarios();

  const runs =
    listMembershipBillingSimulationRuns();

  const simulatedEvidence =
    listMembershipBillingSimulationEvidence();

  const latestRuns =
    latestRunsByScenario(runs);

  const requiredRuns =
    scenarios.map(
      scenario =>
        latestRuns.get(scenario.id) ??
        null,
    );

  const allRequiredPassed =
    requiredRuns.length > 0 &&
    requiredRuns.every(
      run =>
        run?.passed === true &&
        run.cleanupCompleted &&
        !run.productionMutationOccurred,
    );

  const anyRequiredFailed =
    requiredRuns.some(
      run =>
        run !== null &&
        !run.passed,
    );

  const simulatedRuntime:
    CertificationState =
      allRequiredPassed
        ? 'pass'
        : anyRequiredFailed
          ? 'fail'
          : 'not_run';

  const projectedCertification = {
    ...workspace.certification,
    simulatedRuntime,
  };

  const projectedWorkspace:
    SystemWorkspaceDefinition = {
      ...workspace,
      certification:
        projectedCertification,
      statusSignals: {
        ...workspace.statusSignals,
        currentEvidenceAvailable:
          lockedEvidence.length +
            simulatedEvidence.length >
          0,
        criticalFailure:
          anyRequiredFailed,
        nonCriticalIssue:
          !allRequiredPassed ||
          projectedCertification
            .deployedRuntime !== 'pass' ||
          projectedCertification
            .liveTransaction !== 'pass' ||
          projectedCertification
            .recovery !== 'pass',
        reason: anyRequiredFailed
          ? 'A required Membership simulation failed. Review the scenario evidence before certification.'
          : allRequiredPassed
            ? 'All required Membership simulations passed. Deployed runtime, live transaction, and recovery certification remain pending.'
            : 'Membership simulation package is registered. Required scenarios have not all been run.',
      },
    };

  const evidence =
    projectSystemEvidence(
      workspace.id,
      [
        ...simulatedEvidence,
        ...lockedEvidence,
      ],
    );

  const certification =
    projectSystemCertification(
      workspace.id,
      projectedCertification,
    );

  const status =
    projectSystemStatus(
      projectedWorkspace,
    );

  return {
    systemId: workspace.id,
    status,
    evidence,
    certification,

    simulator: {
      engineInstalled: true,
      enabled:
        workspace.simulatorPolicy.enabled,
      productionWritesAllowed:
        workspace.simulatorPolicy
          .productionWritesAllowed,
      supportedModes:
        workspace.simulatorPolicy
          .supportedModes,
      resetRequired:
        workspace.simulatorPolicy
          .resetRequired,
      registeredScenarioCount:
        scenarios.length,
      scenarioExecutionAvailable:
        scenarios.length > 0,
      requiredScenarioCount:
        scenarios.length,
      passedRequiredCount:
        requiredRuns.filter(
          run => run?.passed,
        ).length,
      allRequiredPassed,
      scenarios,
      runs,
    },

    ai: {
      contractInstalled: true,
      enabled:
        workspace.aiPolicy.enabled,
      maximumLevel:
        workspace.aiPolicy
          .maximumAutonomousLevel,
      productionExecutionAllowed:
        workspace.aiPolicy
          .mayExecuteProductionWrites,
      registeredAssistantCount: 0,
      assistanceAvailable: false,
      supportedCapabilities: [
        'Explain',
        'Investigate',
        'Generate scenario',
        'Recommend repair',
        'Prepare change',
      ],
    },

    incidents: {
      activeCount:
        runs.filter(
          run => !run.passed,
        ).length,
      liveIncidentFeedInstalled: false,
      reason:
        'Simulation failures are visible here; the live Membership incident feed is not installed yet.',
    },

    repairReadiness: {
      orchestratorInstalled: true,
      registeredRepairCount: 0,
      approvedExecutionLevelAvailable: true,
      autonomousRepairEnabled: false,
      autonomousCertifiedRepairCount: 0,
      currentMaximumLevel: 4,
      reason:
        'The governed repair orchestrator is installed. No Membership repair handler is registered, so execution and autonomous repair remain unavailable.',
    },
  };
}
